import styled from "styled-components";
import { IoIosArrowForward } from "react-icons/io";
import { SkeletonLine } from "../SkeletonLine";
import DataTable from "../DataTable";

const PaginatedTableContainer = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 12px;
    box-sizing: border-box;
    gap: 10px;

    @media (min-width: 900px) {
        padding: 15px;
    }
`
const TableWrapper = styled.div`
    flex: 1;
    width: 100%;
    overflow: auto;
    border: 1px solid var(--ctrl-border, #ddd);
    background-color: var(--ctrl-surface-muted, #f3f3f3);
    border-radius: 8px;
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? "none" : "block")};
    min-height: 0;

    @media (min-width: 900px) {
        display: block;
    }
    
    &::-webkit-scrollbar {
        height: 8px;
        width: 12px;
        background-color: var(--ctrl-scrollbar-track, #f0f0f0);
    }
    
    &::-webkit-scrollbar-thumb {
        background-color: var(--ctrl-scrollbar, #c1c1c1);
        border-radius: 4px;
        border: 3px solid var(--ctrl-scrollbar-track, #f0f0f0);
    }
`

const MobileCardsWrapper = styled.div`
    flex: 1;
    width: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;

    @media (min-width: 900px) {
        display: none;
    }
`

const MobileCard = styled.button`
    width: 100%;
    text-align: left;
    border: 1px solid var(--ctrl-border, #d3d3d3);
    background: var(--ctrl-surface, #fff);
    border-radius: 10px;
    padding: 12px 14px;
    cursor: pointer;
    color: var(--ctrl-text, #333);
    font: inherit;
    transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.1s ease;

    &:active {
        transform: scale(0.99);
    }

    &:hover {
        border-color: #E8A317;
    }
`

const MobileCardTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 4px;
`

const MobileCardMeta = styled.div`
    font-size: 12px;
    color: var(--ctrl-text-muted, #667788);
    line-height: 1.45;
`

const MobileEmpty = styled.div`
    padding: 24px 12px;
    text-align: center;
    color: var(--ctrl-text-muted, #667788);
    font-size: 14px;
    border: 1px dashed var(--ctrl-border, #d3d3d3);
    border-radius: 10px;
`

const FooterWrapper = styled.div`
    width: 100%;
    min-height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`
const PaginationWrapper = styled.div`
    height: 30px;
    margin-left: auto;
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-radius: 4px;
    display: flex;
    box-sizing: border-box;
`
const ChangePageBtn = styled.div`
    width: 30px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color .3s;
    color: var(--ctrl-icon, #333);
    &:hover {
        background-color: var(--ctrl-surface-muted, #f0f0f0);
    }
`
const ChangePageIcon = styled(IoIosArrowForward)`
    width: 20px;
    height: 20px;
    color: var(--ctrl-icon, #333);
    transform: ${({ direction }) => direction === 'prev' ? 'rotate(180deg)' : 'none'};
`
const PageNumber = styled.div`
    height: 100%;
    width: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-right: 1.2px solid var(--ctrl-border, #d3d3d3);
    box-sizing: border-box;
    font-size: 16px;
    & p{
        margin: 0;
        color: var(--ctrl-text, #333);
    }
`
const TotalItems = styled.div`
    margin-left: 0;
    font-size: 13px;
    color: var(--ctrl-text, #333);
    & p{
        margin: 0;
        color: var(--ctrl-text, #333);
    }

    @media (min-width: 900px) {
        margin-left: 15px;
        font-size: 14px;
    }
`
const TotalPages = styled.div`
    margin-left: 8px;
    font-size: 13px;
    color: var(--ctrl-text, #333);
    & p{
        margin: 0;
        color: var(--ctrl-text, #333);
    }

    @media (min-width: 900px) {
        margin-left: 15px;
        font-size: 14px;
    }
`

/**
 * @param {{
 *   pagina: number,
 *   setPagina: (n: number) => void,
 *   children: React.ReactNode,
 *   thead: React.ReactNode,
 *   loading?: boolean,
 *   colunas?: number,
 *   totalItems?: number,
 *   totalPages?: number,
 *   mobileCards?: Array<{ key: string|number, title: string, meta?: string, onClick?: () => void }>|null,
 * }} props
 */
const PaginatedTable = ({
  pagina,
  setPagina,
  children,
  thead,
  loading,
  colunas = 4,
  totalItems = 0,
  totalPages = 0,
  mobileCards = null,
}) => {
    const nextPage = () => {
        if (totalPages && pagina >= totalPages) return;
        setPagina(pagina + 1);
    }

    const prevPage = () => {
        if (pagina > 1) {
            setPagina(pagina - 1);
        }
    }

    const hasMobileCards = Array.isArray(mobileCards);

    return (
        <PaginatedTableContainer>
            <TableWrapper $hideOnMobile={hasMobileCards}>
                <DataTable>
                    <thead>
                        {thead}
                    </thead>
                    {loading ? (
                        <tbody>
                            {[...Array(12)].map((_, index) => (
                                <tr key={index}>
                                    {[...Array(colunas)].map((_, colIndex) => (
                                        <td key={colIndex}>
                                            <SkeletonLine />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ) : (
                        children
                    )}
                </DataTable>
            </TableWrapper>

            {hasMobileCards && (
                <MobileCardsWrapper>
                    {loading ? (
                        [...Array(6)].map((_, index) => (
                            <MobileCard key={index} as="div" style={{ cursor: "default" }}>
                                <SkeletonLine />
                                <div style={{ height: 8 }} />
                                <SkeletonLine />
                            </MobileCard>
                        ))
                    ) : mobileCards.length === 0 ? (
                        <MobileEmpty>Nenhum registro encontrado.</MobileEmpty>
                    ) : (
                        mobileCards.map((card) => (
                            <MobileCard
                                key={card.key}
                                type="button"
                                onClick={card.onClick}
                            >
                                <MobileCardTitle>{card.title}</MobileCardTitle>
                                {card.meta && <MobileCardMeta>{card.meta}</MobileCardMeta>}
                            </MobileCard>
                        ))
                    )}
                </MobileCardsWrapper>
            )}

            <FooterWrapper>
                <TotalItems>
                    <p>Total de itens: {totalItems}</p>
                </TotalItems>
                <TotalPages>
                    <p>Página {pagina} de {totalPages || 1}</p>
                </TotalPages>
                <PaginationWrapper>
                    <ChangePageBtn onClick={prevPage}>
                        <ChangePageIcon direction="prev" />
                    </ChangePageBtn>
                    <PageNumber>{pagina}</PageNumber>
                    <ChangePageBtn onClick={nextPage}>
                        <ChangePageIcon direction="next" />
                    </ChangePageBtn>
                </PaginationWrapper>
            </FooterWrapper>
        </PaginatedTableContainer>
    )
}

export default PaginatedTable;
